import {
  FaActionTracker,
  StructuredLogger,
} from '@brighthub/structured-logging';
import {
  DestroyRef,
  Injectable,
  computed,
  inject,
  signal,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CARE_PLAN_CLIENT_CONTEXT } from '../care-plan-client-context.token';
import { CarePlanStore } from '../care-plan.store';
import { CarePlanCase } from '../care-plan.model';
import { caseApiErrorMessage } from '../cases/api/case-api-error';
import { OPTION_API } from '../options/api/option-api.token';
import { CarePlanOption } from '../options/options.model';
import { OUTCOME_API } from './api/outcome-api.token';
import { OutcomeChangesService } from './outcome-changes.service';
import {
  OutcomeCard,
  OutcomeListPayload,
  OutcomeType,
  SaveOutcomeRequest,
} from './api/outcome-api.model';

interface OutcomeScope {
  caseId: number | null;
  clientId: number;
  guid: string;
  carePlan: CarePlanStore;
}

@Injectable()
export class OutcomesStore {
  private readonly _actionLogs = new FaActionTracker(inject(StructuredLogger));
  private readonly api = inject(OUTCOME_API);
  private readonly optionApi = inject(OPTION_API);
  private readonly context = inject(CARE_PLAN_CLIENT_CONTEXT);
  private readonly changes = inject(OutcomeChangesService);
  private readonly scope = signal<OutcomeScope | null>(null);
  private destroyed = false;
  private loadGeneration = 0;
  private referenceGeneration = 0;
  private optionsGeneration = 0;
  private readonly eligibilityTime = signal(Date.now());

  readonly movePending = signal(false);
  private move: {
    scope: OutcomeScope;
    outcomeId: number;
    caseId: number;
    optionIds: number[];
    sourceIds: number[];
    content?: Omit<SaveOutcomeRequest, 'caseId'>;
    contentSaved: boolean;
    unlinked: boolean;
    linkRequested: boolean;
    writeAttempted: boolean;
    stage: 'preflight' | 'update' | 'unlink' | 'link';
  } | null = null;

  readonly session = this.scope.asReadonly();

  readonly data = signal<OutcomeListPayload | null>(null);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly types = signal<readonly OutcomeType[]>([]);
  readonly options = signal<CarePlanOption[]>([]);
  readonly referenceLoading = signal(false);
  readonly referenceError = signal<string | null>(null);
  readonly saving = signal(false);
  readonly linkCaseId = signal<number | null>(null);
  readonly linkOptionsLoading = signal(false);
  readonly linkOptionsError = signal<string | null>(null);
  readonly clientScoped = computed(() => this.scope()?.caseId === null);
  readonly outcomes = computed(() => {
    const outcomes = this.data()?.outcomes ?? [];
    // AIM hydrates linkedOptions from every active outcome_case_links row,
    // including links to cases outside the currently displayed case list.
    return this.clientScoped()
      ? outcomes.filter(
          (outcome) =>
            outcome.linkedOptions.length === 0 && outcome.atiValue !== null,
        )
      : outcomes;
  });
  readonly caseChoices = computed(() => {
    const scope = this.scope();
    if (!scope || !this.current(scope)) return [];
    return scope.carePlan
      .cases()
      .filter((item) => this.linkableCase(item, this.eligibilityTime()))
      .sort((a, b) => {
        const order = ['in_progress', 'monitor', 'closed'];
        return (
          order.indexOf(a.status.toLowerCase()) -
            order.indexOf(b.status.toLowerCase()) ||
          (Date.parse(b.createdAt ?? '') || 0) -
            (Date.parse(a.createdAt ?? '') || 0)
        );
      })
      .map((item) => ({
        label: `${item.title} (#${item.id})`,
        value: Number(item.id),
      }));
  });
  readonly currentCaseChoice = computed(() => {
    const scope = this.scope();
    if (!scope || !this.current(scope) || scope.caseId === null) return null;
    const item = scope.carePlan
      .cases()
      .find((item) => item.id === String(scope.caseId));
    return {
      label: item?.title
        ? `${item.title} (#${scope.caseId})`
        : `Case #${scope.caseId}`,
      value: scope.caseId,
    };
  });
  readonly totalAti = computed(() => this.data()?.atiSummary.totalAti ?? 0);
  readonly canWrite = computed(() => {
    const scope = this.scope();
    if (
      !scope ||
      !this.current(scope) ||
      !scope.carePlan.isLive() ||
      !scope.carePlan.canEditLiveCases()
    )
      return false;
    if (scope.caseId === null) return true;
    const status = scope.carePlan
      .cases()
      .find((c) => c.id === String(scope.caseId))
      ?.status.toLowerCase();
    return (
      !!status &&
      ['planned', 'in_progress', 'monitor', 'closed'].includes(status) &&
      !scope.carePlan.isCaseSaving(String(scope.caseId))
    );
  });

  constructor() {
    this.changes.changed.pipe(takeUntilDestroyed()).subscribe((change) => {
      const scope = this.scope();
      if (
        scope &&
        this.current(scope) &&
        change.origin !== this &&
        change.clientId === scope.clientId &&
        change.guid === scope.guid
      )
        void this.load();
    });
    inject(DestroyRef).onDestroy(() => {
      this.destroyed = true;
    });
  }

  async bind(caseId: string | null, carePlan: CarePlanStore): Promise<void> {
    const clientId = this.context.clientId();
    const guid = this.context.activeGuid();
    this.scope.set(null);
    this.move = null;
    this.movePending.set(false);
    this.data.set(null);
    this.options.set([]);
    this.types.set([]);
    this.error.set(null);
    this.referenceError.set(null);
    this.loading.set(false);
    this.referenceLoading.set(false);
    this.linkCaseId.set(null);
    this.linkOptionsLoading.set(false);
    this.linkOptionsError.set(null);
    ++this.referenceGeneration;
    ++this.optionsGeneration;
    if (
      !guid ||
      !clientId ||
      !Number.isSafeInteger(clientId) ||
      clientId <= 0 ||
      carePlan.activeGuid() !== guid ||
      (caseId !== null &&
        (!Number.isSafeInteger(Number(caseId)) || Number(caseId) <= 0))
    )
      return;
    this.scope.set({
      caseId: caseId === null ? null : Number(caseId),
      clientId,
      guid,
      carePlan,
    });
    await Promise.all([this.load(), this.loadReferences()]);
  }

  async load(): Promise<void> {
    const scope = this.scope();
    if (!scope || !this.current(scope)) return;
    this.eligibilityTime.set(Date.now());
    const generation = ++this.loadGeneration;
    this.loading.set(true);
    this.error.set(null);
    try {
      const data = await firstValueFrom(
        scope.caseId === null
          ? this.api.listForClient(scope.clientId)
          : this.api.list(scope.caseId),
      );
      if (this.current(scope) && generation === this.loadGeneration)
        this.data.set(data);
    } catch (error) {
      if (this.current(scope) && generation === this.loadGeneration) {
        this.data.set(null);
        this.error.set(
          caseApiErrorMessage(
            error,
            'Outcomes could not be loaded. Please retry.',
          ),
        );
      }
    } finally {
      if (this.current(scope) && generation === this.loadGeneration)
        this.loading.set(false);
    }
  }

  async loadReferences(refresh = false): Promise<void> {
    const scope = this.scope();
    if (!scope || !this.current(scope)) return;
    const generation = ++this.referenceGeneration;
    // Source references may update options only while this view owns them.
    // A destination selection or close invalidates both kinds of option request.
    const sourceOptions =
      scope.caseId !== null &&
      (this.linkCaseId() === null || this.linkCaseId() === scope.caseId);
    const optionsGeneration = sourceOptions
      ? ++this.optionsGeneration
      : this.optionsGeneration;
    if (sourceOptions) {
      this.linkOptionsLoading.set(false);
      this.linkOptionsError.set(null);
    }
    this.referenceLoading.set(true);
    this.referenceError.set(null);
    // Type labels remain useful even if plan options are temporarily unavailable.
    const results = await Promise.allSettled([
      firstValueFrom(this.api.types(refresh)),
      sourceOptions && scope.caseId !== null
        ? firstValueFrom(this.optionApi.listOptions(String(scope.caseId)))
        : Promise.resolve([]),
    ]);
    if (!this.current(scope) || generation !== this.referenceGeneration) return;
    if (results[0].status === 'fulfilled') this.types.set(results[0].value);
    const ownsOptions =
      sourceOptions && optionsGeneration === this.optionsGeneration;
    if (ownsOptions && results[1].status === 'fulfilled')
      this.options.set(results[1].value);
    if (
      results[0].status === 'rejected' ||
      (ownsOptions && results[1].status === 'rejected') ||
      (results[0].status === 'fulfilled' && results[0].value.length === 0)
    ) {
      this.referenceError.set(
        'Outcome types or linked options could not be loaded. Please retry.',
      );
    }
    this.referenceLoading.set(false);
  }

  label(code: string): string {
    return this.types().find((type) => type.code === code)?.label ?? code;
  }

  title(outcome: OutcomeCard): string {
    return (
      outcome.name?.trim() ||
      outcome.description?.trim() ||
      this.label(outcome.outcomeTypeCode)
    );
  }

  canEdit(outcome: OutcomeCard): boolean {
    return (
      this.canWrite() &&
      !outcome.invalidated &&
      (this.clientScoped()
        ? outcome.linkedOptions.length === 0 && outcome.editAllowed !== false
        : outcome.editAllowed === true)
    );
  }

  async loadCaseOptions(caseId: number | null): Promise<void> {
    this.eligibilityTime.set(Date.now());
    const scope = this.scope();
    const generation = ++this.optionsGeneration;
    this.linkCaseId.set(caseId);
    this.options.set([]);
    this.linkOptionsError.set(null);
    this.linkOptionsLoading.set(false);
    if (!scope || !this.current(scope) || caseId === null) return;
    if (
      caseId !== scope.caseId &&
      !this.caseChoices().some((item) => item.value === caseId)
    ) {
      this.linkOptionsError.set('Select an available case for this client.');
      return;
    }
    this.linkOptionsLoading.set(true);
    try {
      const options = await firstValueFrom(
        this.optionApi.listOptions(String(caseId)),
      );
      if (this.current(scope) && generation === this.optionsGeneration)
        this.options.set(options);
    } catch (error) {
      if (this.current(scope) && generation === this.optionsGeneration)
        this.linkOptionsError.set(
          caseApiErrorMessage(
            error,
            'Case options could not be loaded. Please retry.',
          ),
        );
    } finally {
      if (this.current(scope) && generation === this.optionsGeneration)
        this.linkOptionsLoading.set(false);
    }
  }

  resetCaseOptions(): void {
    ++this.optionsGeneration;
    this.linkCaseId.set(null);
    this.linkOptionsLoading.set(false);
    this.linkOptionsError.set(null);
    this.options.set([]);
  }

  async getForView(outcomeId: number): Promise<OutcomeCard> {
    const scope = this.scope();
    if (!scope || !this.current(scope))
      throw new Error('This client is no longer selected.');
    let outcome: OutcomeCard | undefined;
    if (scope.caseId === null) {
      // The detail GET requires a case. Refresh the client list to verify this
      // outcome still belongs to the selected client and remains unlinked.
      const generation = ++this.loadGeneration;
      const data = await firstValueFrom(this.api.listForClient(scope.clientId));
      if (!this.current(scope) || generation !== this.loadGeneration)
        throw new Error('Outcomes changed. Please refresh and try again.');
      this.data.set(data);
      outcome = data.outcomes.find(
        (item) =>
          item.outcomeId === outcomeId && item.linkedOptions.length === 0,
      );
    } else {
      outcome = await firstValueFrom(this.api.get(scope.caseId, outcomeId));
    }
    if (!this.current(scope) || !outcome)
      throw new Error(
        'This outcome is no longer available in this view. Refresh outcomes.',
      );
    return outcome;
  }

  async getForEdit(outcomeId: number): Promise<OutcomeCard> {
    const scope = this.requiredScope();
    const outcome = await this.getForView(outcomeId);
    if (!this.current(scope) || !this.canEdit(outcome))
      throw new Error(
        'This outcome can no longer be edited. Refresh the outcomes to see its current state.',
      );
    return outcome;
  }

  async save(
    value: Omit<SaveOutcomeRequest, 'caseId'>,
    outcome?: OutcomeCard,
    targetCaseId?: number | null,
  ): Promise<void> {
    return this._actionLogs.run(
      outcome ? 'care_plan.outcome.update' : 'care_plan.outcome.create',
      `${this.scope()?.guid}:${this.scope()?.caseId}:${outcome?.outcomeId ?? 'new'}`,
      async (logAttempt) => {
        logAttempt.identifiers = {
          client_guid: this.scope()?.guid,
          case_id: this.scope()?.caseId,
          outcome_id: outcome?.outcomeId,
        };
        logAttempt.stage = 'save';
        const scope = this.requiredScope(outcome);
        if (this.saving())
          throw new Error('An outcome is already being saved.');
        this.saving.set(true);
        try {
          if (scope.caseId === null) {
            if (!outcome) throw new Error('Record new outcomes from a case.');
            await this.getForEdit(outcome.outcomeId);
            if (!this.current(scope))
              throw new Error('This client is no longer selected.');
            if (targetCaseId != null)
              this.assertLinkSelection(targetCaseId, value.linkedOptions);
          }
          const caseId = scope.caseId ?? targetCaseId;
          // Full-form PATCH supports creating the target case's option links as
          // part of the edit, so linking and content changes use one request.
          const body: SaveOutcomeRequest = {
            ...value,
            ...(caseId != null ? { caseId } : {}),
          };
          // Unlinked Outcomes permits content/link changes, never an ATI update.
          // Omission also avoids overwriting a newer upstream ATI correction.
          if (scope.caseId === null) delete body.atiValue;
          await firstValueFrom(
            outcome
              ? logAttempt.request(this.api.update(outcome.outcomeId, body))
              : logAttempt.request(
                  this.api.create({ ...body, clientId: scope.clientId }),
                ),
          );
          logAttempt.completedCount = 1;
          // A failed refresh must not turn a successful POST into a retryable save.
          if (this.current(scope)) {
            await this.load();
            this.notifyChange(scope);
          }
        } finally {
          this.saving.set(false);
        }
      },
    );
  }

  async invalidate(outcome: OutcomeCard, reason: string): Promise<void> {
    return this._actionLogs.run(
      'care_plan.outcome.invalidate',
      `${this.scope()?.guid}:${this.scope()?.caseId}:${outcome?.outcomeId ?? 'new'}`,
      async (logAttempt) => {
        logAttempt.identifiers = {
          client_guid: this.scope()?.guid,
          case_id: this.scope()?.caseId,
          outcome_id: outcome?.outcomeId,
        };
        logAttempt.stage = 'save';
        const scope = this.requiredScope(outcome);
        if (this.saving())
          throw new Error('An outcome is already being saved.');
        this.saving.set(true);
        try {
          if (scope.caseId === null) await this.getForEdit(outcome.outcomeId);
          await firstValueFrom(
            logAttempt.request(this.api.invalidate(outcome.outcomeId, reason)),
          );
          logAttempt.completedCount = 1;
          // Invalidation returns links across cases: refresh the case-scoped list.
          if (this.current(scope)) {
            await this.load();
            this.notifyChange(scope);
          }
        } finally {
          this.saving.set(false);
        }
      },
    );
  }

  async link(
    outcome: OutcomeCard,
    caseId: number,
    optionIds: number[],
  ): Promise<void> {
    return this._actionLogs.run(
      'care_plan.outcome.link',
      `${this.scope()?.guid}:${this.scope()?.caseId}:${outcome?.outcomeId ?? 'new'}`,
      async (logAttempt) => {
        logAttempt.identifiers = {
          client_guid: this.scope()?.guid,
          case_id: this.scope()?.caseId,
          outcome_id: outcome?.outcomeId,
        };
        logAttempt.stage = 'link';
        const scope = this.requiredScope(outcome);
        if (scope.caseId !== null)
          throw new Error('This outcome already has a case.');
        if (this.saving())
          throw new Error('An outcome is already being saved.');
        this.saving.set(true);
        try {
          await this.getForEdit(outcome.outcomeId);
          this.assertLinkSelection(caseId, optionIds);
          await firstValueFrom(
            logAttempt.request(
              this.api.link(outcome.outcomeId, {
                caseId,
                linkedOptions: optionIds,
              }),
            ),
          );
          if (this.current(scope)) {
            await this.load();
            this.notifyChange(scope);
          }
        } finally {
          this.saving.set(false);
        }
      },
    );
  }

  private assertLinkSelection(caseId: number, optionIds: number[]): void {
    // Computed choices can outlive the 30-day boundary while a modal is open.
    // Refresh the clock at submission rather than trusting the cached choices.
    this.eligibilityTime.set(Date.now());
    if (
      !this.caseChoices().some((item) => item.value === caseId) ||
      this.linkCaseId() !== caseId ||
      this.linkOptionsLoading() ||
      this.linkOptionsError() ||
      optionIds.length === 0 ||
      optionIds.some(
        (id) => !this.options().some((option) => Number(option.id) === id),
      )
    )
      throw new Error(
        'Select an available case and at least one of its options.',
      );
  }

  async unlink(outcome: OutcomeCard): Promise<void> {
    return this._actionLogs.run(
      'care_plan.outcome.unlink',
      `${this.scope()?.guid}:${this.scope()?.caseId}:${outcome?.outcomeId ?? 'new'}`,
      async (logAttempt) => {
        logAttempt.identifiers = {
          client_guid: this.scope()?.guid,
          case_id: this.scope()?.caseId,
          outcome_id: outcome?.outcomeId,
        };
        logAttempt.stage = 'unlink';
        const scope = this.requiredScope(outcome);
        if (scope.caseId === null)
          throw new Error('This outcome is not on a case.');
        if (this.saving())
          throw new Error('An outcome is already being saved.');
        this.saving.set(true);
        let requested = false;
        try {
          const fresh = await this.getForEdit(outcome.outcomeId);
          if (!this.current(scope) || !this.canEdit(fresh))
            throw new Error(
              'This case or outcome is no longer available for editing.',
            );
          const originalIds = [
            ...new Set(
              outcome.linkedOptions.map((option) => option.linkedOptionId),
            ),
          ].sort((a, b) => a - b);
          const freshIds = [
            ...new Set(
              fresh.linkedOptions.map((option) => option.linkedOptionId),
            ),
          ].sort((a, b) => a - b);
          if (
            !freshIds.length ||
            JSON.stringify(originalIds) !== JSON.stringify(freshIds)
          )
            throw new Error(
              'This outcome’s links have changed. Close and reopen it before unlinking.',
            );
          // Only this case's confirmed triples are removed. Other case links stay.
          requested = true;
          await firstValueFrom(
            logAttempt.request(
              this.api.unlink(outcome.outcomeId, {
                caseId: scope.caseId,
                linkedOptions: freshIds,
              }),
            ),
          );
          if (this.current(scope)) {
            await this.load();
            this.notifyChange(scope);
          }
        } catch (error) {
          if (requested && this.current(scope)) {
            // A write may commit before its response fails. Reconcile rather than
            // inviting another unlink of already removed triples.
            await this.load();
            if (!this.current(scope)) throw error;
            this.notifyChange(scope);
            const data = this.data();
            if (
              data &&
              !data.outcomes.some(
                (item) => item.outcomeId === outcome.outcomeId,
              )
            )
              return;
          }
          throw error;
        } finally {
          this.saving.set(false);
        }
      },
    );
  }

  moveProgress(): { stage: string; sourceUnlinked: boolean } {
    return {
      stage: this.move?.stage ?? 'preflight',
      sourceUnlinked: this.move?.unlinked ?? false,
    };
  }

  clearMoveDraft(): void {
    this.move = null;
    this.movePending.set(false);
  }

  async reassign(
    outcome: OutcomeCard,
    caseId: number,
    optionIds: number[],
    content?: Omit<SaveOutcomeRequest, 'caseId'>,
  ): Promise<void> {
    return this._actionLogs.run(
      'care_plan.outcome.reassign',
      `${this.scope()?.guid}:${this.scope()?.caseId}:${outcome?.outcomeId ?? 'new'}`,
      async (logAttempt) => {
        logAttempt.identifiers = {
          client_guid: this.scope()?.guid,
          case_id: this.scope()?.caseId,
          outcome_id: outcome?.outcomeId,
        };
        logAttempt.identifiers['target_case_id'] = caseId;
        logAttempt.stage = 'preflight';
        logAttempt.operationCount = content ? 3 : 2;
        const scope = this.requiredScope(outcome);
        if (scope.caseId === null || scope.caseId === caseId)
          throw new Error('Select another case.');
        if (this.saving())
          throw new Error('An outcome is already being saved.');
        this.assertLinkSelection(caseId, optionIds);
        if (
          this.move &&
          (this.move.scope !== scope ||
            this.move.outcomeId !== outcome.outcomeId ||
            this.move.caseId !== caseId ||
            JSON.stringify(this.move.optionIds) !== JSON.stringify(optionIds))
        )
          throw new Error(
            'Retry the pending case move before changing the selection.',
          );
        const assertCurrent = () => {
          if (!this.current(scope))
            throw new Error('This client is no longer selected.');
          this.requiredScope(outcome);
        };
        this.saving.set(true);
        let changed = false;
        try {
          if (!this.move) {
            const fresh = await this.getForEdit(outcome.outcomeId);
            this.requiredScope(fresh);
            this.assertLinkSelection(caseId, optionIds);
            const ids = (card: OutcomeCard) =>
              [
                ...new Set(
                  card.linkedOptions.map((option) => option.linkedOptionId),
                ),
              ].sort((a, b) => a - b);
            const sourceIds = ids(fresh);
            if (
              !sourceIds.length ||
              JSON.stringify(sourceIds) !== JSON.stringify(ids(outcome))
            )
              throw new Error(
                'This outcome’s links have changed. Close and reopen it before moving.',
              );
            this.move = {
              scope,
              outcomeId: outcome.outcomeId,
              caseId,
              optionIds: [...optionIds],
              sourceIds,
              content,
              contentSaved: !content,
              unlinked: false,
              linkRequested: false,
              writeAttempted: false,
              stage: 'preflight',
            };
            this.movePending.set(true);
          }
          const move = this.move;
          // Re-read the client record on retries too: source detail no longer exists
          // once unlink has committed, but invalidation and client ownership still matter.
          const clientData = await firstValueFrom(
            this.api.listForClient(scope.clientId),
          );
          const current = clientData.outcomes.find(
            (item) => item.outcomeId === outcome.outcomeId,
          );
          if (
            !this.current(scope) ||
            !this.canWrite() ||
            !current ||
            current.invalidated ||
            current.editAllowed === false
          )
            throw new Error(
              'This case or outcome is no longer available for editing.',
            );
          this.assertLinkSelection(caseId, optionIds);
          if (!move.unlinked) {
            const source = await firstValueFrom(this.api.list(scope.caseId));
            assertCurrent();
            const remaining = source.outcomes.find(
              (item) => item.outcomeId === outcome.outcomeId,
            );
            const remainingIds =
              remaining?.linkedOptions.map((option) => option.linkedOptionId) ??
              [];
            if (
              !move.writeAttempted &&
              (!remaining ||
                JSON.stringify([...remainingIds].sort((a, b) => a - b)) !==
                  JSON.stringify(move.sourceIds))
            )
              throw new Error(
                'This outcome’s links have changed. Close and reopen it before moving.',
              );
            if (
              remaining &&
              (!this.canEdit(remaining) ||
                !remainingIds.length ||
                remainingIds.some((id) => !move.sourceIds.includes(id)))
            )
              throw new Error(
                'This outcome’s links have changed. Close and reopen it before moving.',
              );
            if (remaining) {
              if (!move.contentSaved && move.content) {
                changed = true;
                move.writeAttempted = true;
                move.stage = 'update';
                logAttempt.stage = 'update';
                await firstValueFrom(
                  logAttempt.request(
                    this.api.update(outcome.outcomeId, {
                      ...move.content,
                      caseId: scope.caseId,
                      linkedOptions: remainingIds,
                    }),
                  ),
                );
                move.contentSaved = true;
                logAttempt.completedCount++;
              }
              assertCurrent();
              this.assertLinkSelection(caseId, optionIds);
              changed = true;
              move.writeAttempted = true;
              move.stage = 'unlink';
              logAttempt.stage = 'unlink';
              try {
                await firstValueFrom(
                  logAttempt.request(
                    this.api.unlink(outcome.outcomeId, {
                      caseId: scope.caseId,
                      linkedOptions: remainingIds,
                    }),
                  ),
                );
              } catch (error) {
                const sourceAfter = await firstValueFrom(
                  this.api.list(scope.caseId),
                );
                if (
                  !this.current(scope) ||
                  sourceAfter.outcomes.some(
                    (item) => item.outcomeId === outcome.outcomeId,
                  )
                )
                  throw error;
              }
            }
            move.unlinked = true;
            logAttempt.completedCount++;
          }
          assertCurrent();
          this.assertLinkSelection(caseId, optionIds);
          let missing = move.optionIds;
          if (move.linkRequested) {
            // A failed response may hide a committed or partially committed link.
            // Read the destination before retrying to avoid duplicate triples.
            const destination = await firstValueFrom(this.api.list(caseId));
            assertCurrent();
            const linked = destination.outcomes.find(
              (item) => item.outcomeId === outcome.outcomeId,
            );
            if (linked?.invalidated)
              throw new Error('This outcome can no longer be linked.');
            missing = move.optionIds.filter(
              (id) =>
                !linked?.linkedOptions.some(
                  (option) => option.linkedOptionId === id,
                ),
            );
          }
          this.assertLinkSelection(caseId, optionIds);
          if (missing.length) {
            move.stage = 'link';
            logAttempt.stage = 'link';
            move.linkRequested = true;
            changed = true;
            await firstValueFrom(
              logAttempt.request(
                this.api.link(outcome.outcomeId, {
                  caseId,
                  linkedOptions: missing,
                }),
              ),
            );
          }
          this.move = null;
          this.movePending.set(false);
        } catch (error) {
          if (this.move && !this.move.writeAttempted) this.clearMoveDraft();
          if (this.move?.unlinked)
            throw Object.assign(
              new Error(
                'The outcome was unlinked, but the move could not be completed. Retry Save to finish linking it to the selected case.',
              ),
              { cause: error },
            );
          throw error;
        } finally {
          if (this.current(scope)) {
            if (changed) await this.load();
            this.notifyChange(scope);
          }
          this.saving.set(false);
        }
      },
    );
  }

  private notifyChange(scope: OutcomeScope): void {
    this.changes.notify({
      clientId: scope.clientId,
      guid: scope.guid,
      origin: this,
    });
  }

  private requiredScope(outcome?: OutcomeCard): OutcomeScope {
    const scope = this.scope();
    if (
      !scope ||
      !this.current(scope) ||
      !this.canWrite() ||
      (outcome && !this.canEdit(outcome))
    )
      throw new Error(
        'This case or outcome is no longer available for editing.',
      );
    return scope;
  }

  private current(scope: OutcomeScope): boolean {
    return (
      !this.destroyed &&
      this.scope() === scope &&
      this.context.clientId() === scope.clientId &&
      this.context.activeGuid() === scope.guid &&
      scope.carePlan.activeGuid() === scope.guid
    );
  }

  private linkableCase(item: CarePlanCase, now: number): boolean {
    if (
      !Number.isSafeInteger(Number(item.id)) ||
      Number(item.id) <= 0 ||
      this.scope()?.carePlan.isCaseSaving(item.id)
    )
      return false;
    const status = item.status.toLowerCase();
    if (status === 'in_progress' || status === 'monitor') return true;
    if (status !== 'closed') return false;
    const closedAt = Date.parse(item.closedAt ?? '');
    return (
      Number.isFinite(closedAt) &&
      closedAt <= now &&
      now - closedAt <= 30 * 24 * 60 * 60 * 1000
    );
  }
}
